// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getProjectKeyOrIdFromMatchAndQuery } from '@jira/router-resources__utils/src/getProjectKeyOrId.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
// eslint-disable-next-line jira/import-whitelist
import { prefetchApiReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import type { RouterDataContext, ResourceStoreContext } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

type ServiceDeskContextData = {
	portalId: number;
	portalUrl: string;
	customerUrl?: string;
	serviceDeskId: number;
	userRole: string;
	canSeeNewIssueView: boolean;
};

// Sentinel returned when the servicedesk context API returns 404 or 403.
// Both are legitimate "this request has no usable service desk context" signals
// that should not bring down SSR:
//   - 404: the project has no service desk context.
//   - 403: the current user has no permission to the project's service desk
//     (e.g. a work item in a project the user can't access JSM for).
// All permission-gating fields (userRole) are set to uninhabited values so every
// consumer's permission check fails closed.
const SERVICEDESK_CONTEXT_NOT_FOUND_SENTINEL: ServiceDeskContextData = {
	portalId: 0,
	serviceDeskId: 0,
	userRole: '',
	canSeeNewIssueView: false,
	portalUrl: '',
	customerUrl: '',
};

const getData = async (
	{ match, query }: RouterDataContext,
	_storeContext: ResourceStoreContext,
): Promise<ServiceDeskContextData> => {
	const projectKey = getProjectKeyOrIdFromMatchAndQuery({ match, query });
	try {
		const data = await fetchJson<ServiceDeskContextData>(
			`/rest/servicedesk/1/pages/agent/servicedesk/${projectKey}?operation=getProjectKeyOrIdFromMatchAndQuery`,
		);

		setMark(prefetchApiReadyMark);
		return data;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'jiraServicedeskContextResource',
				packageName: 'jiraRouterResourcesServiceDeskContext',
			},
			error,
		});
		log.safeErrorWithoutCustomerData(
			'spa.router.resource.sd.servicedesk.context',
			'fetchData',
			error,
		);
		// 404 (no servicedesk context) and 403 (no permission to this project's
		// service desk) are both expected on the issue view for work items the user
		// can't access JSM for — treat them as "no permission" and return a benign
		// sentinel instead of failing SSR. All other errors (5xx, network, etc.)
		// re-throw so consumers/error boundaries handle them as before.
		if (
			(error?.statusCode === 404 || error?.statusCode === 403) &&
			fg('ssr_servicedesk_context_')
		) {
			return SERVICEDESK_CONTEXT_NOT_FOUND_SENTINEL;
		}
		throw error;
	}
};

export default getData;
